import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { AlertTriangle, CheckCircle, Clock, RefreshCw, Info, AlertCircle, Zap } from 'lucide-react';
import { formatRelativeTime, formatDateInUserTimezone } from '../utils/timezone';
import Pagination from './Pagination';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Incidents = ({ token }) => {
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState('all'); // all, open, resolved
  const [alertTypeFilter, setAlertTypeFilter] = useState('all'); // all, down, up, slow

  useEffect(() => {
    fetchIncidents();
  }, [currentPage, statusFilter, alertTypeFilter]);

  const fetchIncidents = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: itemsPerPage
      };
      
      // Add filters if not 'all'
      if (statusFilter && statusFilter !== 'all') {
        params.status_filter = statusFilter;
      }
      
      if (alertTypeFilter && alertTypeFilter !== 'all') {
        params.alert_type_filter = alertTypeFilter;
      }
      
      const response = await axios.get(`${API}/incidents`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      
      setIncidents(response.data.incidents);
      setTotalPages(response.data.pages);
      setTotalItems(response.data.total);
    } catch (err) {
      setError('Failed to load incidents');
      console.error('Incidents error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const getIncidentIcon = (alertType) => {
    switch (alertType) {
      case 'down':
        return AlertTriangle;
      case 'up':
        return CheckCircle;
      case 'slow':
        return Clock;
      default:
        return AlertTriangle;
    }
  };

  const getIncidentColor = (alertType) => {
    switch (alertType) {
      case 'down':
        return 'text-red-600 bg-red-50';
      case 'up':
        return 'text-green-600 bg-green-50';
      case 'slow':
        return 'text-yellow-600 bg-yellow-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getImpactColor = (userImpact) => {
    switch (userImpact) {
      case 'HIGH':
        return 'text-red-700 bg-red-100 border-red-200';
      case 'MEDIUM':
        return 'text-orange-700 bg-orange-100 border-orange-200';
      case 'LOW':
        return 'text-yellow-700 bg-yellow-100 border-yellow-200';
      default:
        return 'text-gray-700 bg-gray-100 border-gray-200';
    }
  };

  const getImpactIcon = (userImpact) => {
    switch (userImpact) {
      case 'HIGH':
        return AlertTriangle;
      case 'MEDIUM':
        return AlertCircle;
      case 'LOW':
        return Info;
      default:
        return Info;
    }
  };

  const formatDuration = (startTime, endTime) => {
    if (!endTime) return 'Ongoing';
    
    const duration = new Date(endTime) - new Date(startTime);
    const minutes = Math.floor(duration / 60000);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    }
    return `${minutes}m`;
  };

  if (loading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 xl:p-10">
        <div className="animate-pulse">
          <div className="space-y-4 lg:space-y-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-gray-200 h-16 sm:h-20 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 xl:p-10">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 sm:mb-8 space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Incidents</h2>
          <p className="text-gray-600 text-sm sm:text-base">Monitor downtime and outage history</p>
        </div>
        <button
          onClick={fetchIncidents}
          className="flex items-center justify-center space-x-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors text-sm sm:text-base"
        >
          <RefreshCw className="w-4 h-4" />
          <span>Refresh</span>
        </button>
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        {/* Status Filter */}
        <div className="flex gap-2">
          <button
            onClick={() => { setStatusFilter('all'); setCurrentPage(1); }}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              statusFilter === 'all'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All Status
          </button>
          <button
            onClick={() => { setStatusFilter('open'); setCurrentPage(1); }}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              statusFilter === 'open'
                ? 'bg-red-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Open
          </button>
          <button
            onClick={() => { setStatusFilter('resolved'); setCurrentPage(1); }}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              statusFilter === 'resolved'
                ? 'bg-green-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Resolved
          </button>
        </div>

        {/* Alert Type Filter */}
        <div className="flex gap-2">
          <button
            onClick={() => { setAlertTypeFilter('all'); setCurrentPage(1); }}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              alertTypeFilter === 'all'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All Types
          </button>
          <button
            onClick={() => { setAlertTypeFilter('down'); setCurrentPage(1); }}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              alertTypeFilter === 'down'
                ? 'bg-red-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Down
          </button>
          <button
            onClick={() => { setAlertTypeFilter('up'); setCurrentPage(1); }}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              alertTypeFilter === 'up'
                ? 'bg-green-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Recovered
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 text-sm sm:text-base">
          {error}
        </div>
      )}

      {incidents.length === 0 ? (
        <div className="text-center py-8 sm:py-12">
          <div className="bg-green-100 w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" />
          </div>
          <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">No incidents reported</h3>
          <p className="text-gray-600 text-sm sm:text-base px-4">Your monitors are running smoothly! Incidents will appear here when downtime is detected.</p>
        </div>
      ) : (
        <div className="space-y-4 lg:space-y-6">
          {incidents.map((incident) => {
            const Icon = getIncidentIcon(incident.alert_type);
            const colorClass = getIncidentColor(incident.alert_type);
            
            // Extract enhanced analysis from incident details
            const userImpact = incident.details?.user_impact || 'MEDIUM';
            const impactDescription = incident.details?.impact_description;
            const explanation = incident.details?.explanation;
            const errorCategory = incident.details?.error_category;
            const recommendedAction = incident.details?.recommended_action;
            const statusCode = incident.details?.status_code;
            const responseTime = incident.details?.response_time;
            const userAccessible = incident.details?.user_accessible;
            
            const ImpactIcon = getImpactIcon(userImpact);
            const impactColorClass = getImpactColor(userImpact);
            
            return (
              <div key={incident.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
                <div className="flex items-start space-x-3 sm:space-x-4">
                  <div className={`p-2 rounded-lg ${colorClass} flex-shrink-0`}>
                    <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    {/* Monitor Name & URL */}
                    {incident.monitor_name && (
                      <div className="mb-2 pb-2 border-b border-gray-200">
                        <p className="text-sm font-semibold text-gray-900">{incident.monitor_name}</p>
                        {incident.monitor_url && (
                          <p className="text-xs text-gray-500 break-all">{incident.monitor_url}</p>
                        )}
                      </div>
                    )}
                    
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 space-y-2 sm:space-y-0">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                        {incident.alert_type === 'down' ? 'Website Down' :
                         incident.alert_type === 'up' ? 'Website Recovered' :
                         'Performance Issue'}
                      </h3>
                      <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                        <span className={`px-3 py-1 text-xs sm:text-sm rounded-full ${
                          incident.status === 'open' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                        } self-start`}>
                          {incident.status}
                        </span>
                        {incident.status === 'resolved' && (
                          <span className="text-xs sm:text-sm text-gray-500">
                            Duration: {formatDuration(incident.started_at, incident.resolved_at)}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Enhanced User Impact Section */}
                    {incident.alert_type === 'down' && userImpact && (
                      <div className="mb-4">
                        <div className={`flex items-center space-x-2 px-3 py-2 rounded-lg border ${impactColorClass} mb-2`}>
                          <ImpactIcon className="w-4 h-4 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2">
                              <span className="font-semibold text-sm">User Impact: {userImpact}</span>
                              {userAccessible !== undefined && (
                                <span className={`px-2 py-1 text-xs rounded-full ${
                                  userAccessible ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                }`}>
                                  {userAccessible ? '✓ Accessible' : '✗ Inaccessible'}
                                </span>
                              )}
                            </div>
                            {impactDescription && (
                              <p className="text-sm mt-1 font-medium">
                                {impactDescription}
                              </p>
                            )}
                          </div>
                        </div>
                        
                        {explanation && (
                          <div className="text-sm text-gray-700 mb-2 pl-2 border-l-2 border-blue-200 bg-blue-50 py-2 px-3 rounded-r">
                            <strong className="text-blue-800">What happened:</strong> {explanation}
                          </div>
                        )}
                        
                        {recommendedAction && (
                          <div className="text-sm text-gray-700 pl-2 border-l-2 border-green-200 bg-green-50 py-2 px-3 rounded-r">
                            <strong className="text-green-800">💡 Recommended action:</strong> {recommendedAction}
                          </div>
                        )}
                      </div>
                    )}
                    
                    <p className="text-gray-700 mb-3 text-sm sm:text-base break-words">{incident.message}</p>
                    
                    {/* Technical Details */}
                    {incident.alert_type === 'down' && (statusCode || responseTime || errorCategory) && (
                      <div className="bg-gray-50 rounded-lg p-3 mb-3">
                        <h4 className="text-sm font-medium text-gray-800 mb-2">Technical Details</h4>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs text-gray-600">
                          {statusCode && (
                            <div>
                              <span className="font-medium">Status Code:</span> {statusCode}
                            </div>
                          )}
                          {responseTime && (
                            <div>
                              <span className="font-medium">Response Time:</span> {(responseTime * 1000).toFixed(0)}ms
                            </div>
                          )}
                          {errorCategory && (
                            <div>
                              <span className="font-medium">Category:</span> {errorCategory.replace(/_/g, ' ')}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    <div className="text-xs sm:text-sm text-gray-500 space-y-1">
                      <p title={formatDateInUserTimezone(incident.started_at)}>
                        Started: {formatRelativeTime(incident.started_at)}
                      </p>
                      {incident.resolved_at && (
                        <p title={formatDateInUserTimezone(incident.resolved_at)}>
                          Resolved: {formatRelativeTime(incident.resolved_at)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      
      {/* Pagination */}
      {incidents.length > 0 && (
        <div className="mt-6">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            itemsPerPage={itemsPerPage}
            totalItems={totalItems}
            className="rounded-lg shadow-sm border border-gray-200"
          />
        </div>
      )}
    </div>
  );
};

export default Incidents;